import {MemexColumnDataType} from '../../../../../api/columns/contracts/memex-column'
import type {MultiSelectValue} from '../../../../../api/columns/contracts/multi-select'
import type {PersistedOption} from '../../../../../api/columns/contracts/single-select'
import {asMultiSelectValue} from '../../../../../helpers/parsing'
import {sanitizeTextInputHtmlString} from '../../../../../helpers/sanitize'
import type {MultiSelectColumnModel} from '../../../../../models/column-model/custom/multi-select'
import type {TableDataType} from '../../../table-data-type'
import {DataTypeMismatchFailureError} from '../errors'
import type {ClipboardColumnBehavior, ClipboardContent} from '../types'

/**
 * Resolve the selected options for a multi-select value, preserving the order in
 * which the options are configured on the column and dropping ids whose option no
 * longer exists (e.g. it was deleted after the value was saved).
 */
function selectedOptions(
  value: MultiSelectValue | undefined,
  options: ReadonlyArray<PersistedOption>,
): Array<PersistedOption> {
  if (!value || value.options.length === 0 || options.length === 0) return []
  const selectedIds = new Set(value.options.map(o => o.id))
  return options.filter(option => selectedIds.has(option.id))
}

export const behavior: ClipboardColumnBehavior<MultiSelectColumnModel> = {
  readContent: (row: TableDataType, column: MultiSelectColumnModel) => {
    const raw = asMultiSelectValue(row.columns[column.id]) || undefined
    const options = column.settings.options || []
    const matchingOptions = selectedOptions(raw, options)

    return {
      text: matchingOptions.map(o => o.name).join(', '),
      dataType: MemexColumnDataType.MultiSelect,
      // Preserve the column's configured option order and drop stale ids so the copied value
      // matches what the cell renders.
      value: raw ? {options: matchingOptions.map(o => ({id: o.id}))} : raw,
      html: matchingOptions.map(o => sanitizeTextInputHtmlString(o.nameHtml)).join(', '),
    }
  },
  buildUpdate: (content: ClipboardContent | string, targetColumn: MultiSelectColumnModel) => {
    if (typeof content === 'string') return // Do not allow arbitrary pasting for this data type
    // Like the other typed clipboard behaviors (labels, assignees, milestone, …), only accept a
    // matching data type. Anything else is a paste between incompatible columns.
    if (content.dataType !== MemexColumnDataType.MultiSelect) throw new DataTypeMismatchFailureError()
    // Option ids are column-scoped, so a multi-select value can only be pasted back into the
    // column it was copied from. Reject cross-column pastes (mirroring the iteration behavior)
    // rather than resolving by name, which could silently drop options missing from the target.
    if (content.columnId !== targetColumn.id) throw new DataTypeMismatchFailureError()

    const validIds = new Set((targetColumn.settings.options || []).map(o => o.id))
    const optionIds = (content.value?.options ?? []).map(o => o.id).filter(id => validIds.has(id))

    return {
      dataType: MemexColumnDataType.MultiSelect,
      memexProjectColumnId: targetColumn.id,
      value: optionIds.length > 0 ? {options: optionIds.map(id => ({id}))} : undefined,
      isIssueField: targetColumn.isIssueField,
    }
  },
}
